import i18next from 'i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import { SUPPORTED_LOCALES, DEFAULT_LOCALE, LOCALE_STORAGE_KEY } from './locales';

// The 'common' namespace (generic action/status/error/empty/pagination/field strings) is
// auto-loaded at startup; every feature namespace (catalog, loyalty, settings, customers,
// employees, content, app) is lazy-loaded on demand by the first `useTranslation('<ns>')` call
// that needs it. There used to be a second eager 'common-data' namespace reserved for shared
// enum/domain-data labels, but every feature ended up owning its own enum labels in its own
// namespace instead (arguably a better fit — it keeps a feature's translations co-located and
// independently maintainable), so it was removed as dead weight rather than backfilled with
// placeholder content. Re-add it here (and the matching public/locales/*/common-data.json
// files) if a genuinely cross-feature enum label shows up later.
export const DEFAULT_NS = 'common';
export const AUTO_NAMESPACES = ['common'] as const;

const i18n = i18next
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next);

// `init` is called once; the returned promise is used in LocaleProvider to avoid
// rendering the app before at least the 'common' namespace is ready.
export const i18nReadyPromise = i18n.init({
  ns: [...AUTO_NAMESPACES],
  defaultNS: DEFAULT_NS,
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: SUPPORTED_LOCALES,
  load: 'currentOnly', // don't try 'zh' when user picks 'zh-CN'
  backend: {
    loadPath: '/locales/{{lng}}/{{ns}}.json',
  },
  detection: {
    order: ['localStorage', 'navigator', 'htmlTag'],
    lookupLocalStorage: LOCALE_STORAGE_KEY,
    caches: ['localStorage'],
    convertDetectedLanguage: (lng: string) => {
      // Map browser locales to supported ones
      if (lng.startsWith('zh')) return lng.includes('TW') || lng.includes('HK') ? 'zh-TW' : 'zh-CN';
      if (lng.startsWith('ru')) return 'ru';
      if (lng.startsWith('de')) return 'de';
      if (lng.startsWith('tr')) return 'tr';
      if (lng.startsWith('hi')) return 'hi';
      if (lng.startsWith('ja')) return 'ja';
      return 'en';
    },
  },
  interpolation: {
    escapeValue: false, // React already escapes
  },
  react: {
    useSuspense: false, // use loading state instead of Suspense
  },
});

export default i18n;
